import React, { useEffect, useMemo, useState } from "react";
import { api, asArray } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Loader2, Save, RotateCcw, Eye, Send, Mail, Code,
} from "lucide-react";

export const TemplatesAdmin = () => {
    const [templates, setTemplates] = useState([]);
    const [activeKey, setActiveKey] = useState(null);
    const [draft, setDraft] = useState({ subject: "", body_html: "" });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testTo, setTestTo] = useState("");
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewHtml, setPreviewHtml] = useState("");
    const [previewSubject, setPreviewSubject] = useState("");

    const active = useMemo(
        () => templates.find((t) => t.key === activeKey) || null,
        [templates, activeKey],
    );

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/templates");
            const list = asArray(data);
            setTemplates(list);
            if (!activeKey && list.length) setActiveKey(list[0].key);
        } catch {
            toast.error("Erro a carregar templates");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (active) setDraft({ subject: active.subject, body_html: active.body_html });
    }, [active?.key]);

    const dirty = active && (
        draft.subject !== active.subject || draft.body_html !== active.body_html
    );

    const insertVar = (v) => {
        const placeholder = `{{${v}}}`;
        setDraft((d) => ({ ...d, body_html: (d.body_html || "") + placeholder }));
    };

    const save = async () => {
        if (!active) return;
        setSaving(true);
        try {
            const { data } = await api.put(`/admin/templates/${active.key}`, {
                subject: draft.subject,
                body_html: draft.body_html,
            });
            setTemplates((arr) => arr.map((t) => (t.key === data.key ? data : t)));
            toast.success("Template guardado");
        } catch {
            toast.error("Erro a guardar template");
        } finally {
            setSaving(false);
        }
    };

    const resetDefault = async () => {
        if (!active) return;
        if (!window.confirm("Reverter este template para o conteúdo original? As tuas alterações serão perdidas.")) return;
        setResetting(true);
        try {
            const { data } = await api.post(`/admin/templates/${active.key}/reset`);
            setTemplates((arr) => arr.map((t) => (t.key === data.key ? data : t)));
            setDraft({ subject: data.subject, body_html: data.body_html });
            toast.success("Template revertido para o original");
        } catch {
            toast.error("Erro a reverter template");
        } finally {
            setResetting(false);
        }
    };

    const preview = async () => {
        if (!active) return;
        // save first if dirty? we preview the saved version; warn user if dirty
        if (dirty) {
            const ok = window.confirm("Tens alterações por guardar. A pré-visualização usa a versão guardada — guardar agora?");
            if (ok) await save();
        }
        try {
            const { data } = await api.get(`/admin/templates/${active.key}/preview`);
            setPreviewSubject(data.subject);
            setPreviewHtml(data.html);
            setPreviewOpen(true);
        } catch {
            toast.error("Erro a gerar pré-visualização");
        }
    };

    const sendTest = async () => {
        if (!active) return;
        if (!testTo) {
            toast.error("Indica um email para o teste.");
            return;
        }
        if (dirty) {
            const ok = window.confirm("Tens alterações por guardar. O teste usa a versão guardada — guardar agora?");
            if (ok) await save();
        }
        setTesting(true);
        try {
            await api.post(`/admin/templates/${active.key}/test`, { to: testTo });
            toast.success(`Email de teste enviado para ${testTo}`);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Falha no envio.";
            toast.error(msg);
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-stone-500 py-10">
                <Loader2 className="h-4 w-4 animate-spin" /> A carregar templates…
            </div>
        );
    }

    return (
        <div className="grid lg:grid-cols-[280px_1fr] gap-6" data-testid="templates-admin">
            {/* Sidebar — list */}
            <aside className="space-y-2">
                <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">Templates</div>
                {templates.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setActiveKey(t.key)}
                        data-testid={`tpl-item-${t.key}`}
                        className={`w-full text-left rounded-xl ring-1 transition px-4 py-3 flex items-start gap-3 ${
                            activeKey === t.key
                                ? "bg-gold-soft ring-gold/40 text-stone-900"
                                : "bg-white ring-stone-200 hover:ring-gold/30"
                        }`}
                    >
                        <Mail className="h-4 w-4 mt-0.5 text-gold-deep shrink-0" />
                        <div className="min-w-0">
                            <div className="font-medium text-sm leading-tight">{t.name}</div>
                            <div className="text-xs text-stone-500 mt-1 truncate">{t.key}</div>
                        </div>
                    </button>
                ))}
            </aside>

            {/* Editor */}
            <section className="space-y-5">
                {active ? (
                    <>
                        <header className="bg-white ring-1 ring-stone-200 rounded-2xl p-5">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div>
                                    <h2 className="font-serif text-xl text-stone-900">{active.name}</h2>
                                    <p className="text-sm text-stone-500 mt-0.5">{active.description}</p>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Button variant="outline" size="sm" onClick={preview} data-testid="tpl-preview">
                                        <Eye className="h-4 w-4 mr-2" /> Pré-visualizar
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={resetDefault} disabled={resetting} data-testid="tpl-reset">
                                        {resetting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                                        Reverter
                                    </Button>
                                    <Button size="sm" onClick={save} disabled={saving || !dirty} data-testid="tpl-save">
                                        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                                        Guardar
                                    </Button>
                                </div>
                            </div>
                        </header>

                        {/* Variables */}
                        <div className="bg-stone-50 ring-1 ring-stone-200 rounded-2xl p-5">
                            <div className="flex items-center gap-2 mb-3 text-stone-600 text-sm">
                                <Code className="h-4 w-4" />
                                Variáveis disponíveis (clica para inserir):
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {(active.available_vars || []).map((v) => (
                                    <button
                                        key={v}
                                        type="button"
                                        onClick={() => insertVar(v)}
                                        className="px-3 py-1.5 bg-white ring-1 ring-stone-300 hover:ring-gold/50 hover:bg-gold-soft rounded-full text-xs font-mono text-stone-700 transition"
                                    >
                                        {`{{${v}}}`}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Editor */}
                        <div className="bg-white ring-1 ring-stone-200 rounded-2xl p-5 space-y-4">
                            <div>
                                <Label>Assunto</Label>
                                <Input
                                    value={draft.subject}
                                    onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
                                    data-testid="tpl-subject"
                                />
                            </div>
                            <div>
                                <Label>Corpo (HTML)</Label>
                                <Textarea
                                    rows={14}
                                    value={draft.body_html}
                                    onChange={(e) => setDraft((d) => ({ ...d, body_html: e.target.value }))}
                                    className="font-mono text-sm"
                                    data-testid="tpl-body"
                                />
                                <p className="text-xs text-stone-500 mt-2">
                                    Podes usar tags HTML simples (&lt;p&gt;, &lt;br&gt;, &lt;strong&gt;) e variáveis no formato <code className="bg-stone-100 px-1 rounded">{`{{var}}`}</code>. O layout (cabeçalho, rodapé, logotipo) é aplicado automaticamente.
                                </p>
                            </div>
                            {dirty && (
                                <Badge className="bg-amber-100 text-amber-900 ring-1 ring-amber-200">Alterações por guardar</Badge>
                            )}
                        </div>

                        {/* Test send */}
                        <div className="bg-white ring-1 ring-stone-200 rounded-2xl p-5">
                            <div className="mb-3">
                                <div className="font-medium text-stone-800">Enviar email de teste</div>
                                <p className="text-sm text-stone-500">Usa dados de exemplo (Maria Silva) para veres como o email fica.</p>
                            </div>
                            <div className="grid md:grid-cols-[1fr_auto] gap-3">
                                <Input
                                    type="email"
                                    placeholder="destinatario@exemplo.com"
                                    value={testTo}
                                    onChange={(e) => setTestTo(e.target.value)}
                                    data-testid="tpl-test-to"
                                />
                                <Button variant="outline" onClick={sendTest} disabled={testing} data-testid="tpl-test-send">
                                    {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                                    Enviar teste
                                </Button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-stone-500">Seleciona um template à esquerda.</div>
                )}
            </section>

            {/* Preview Modal */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden p-0">
                    <DialogHeader className="px-6 pt-6 pb-2">
                        <DialogTitle className="font-serif text-xl">Pré-visualização</DialogTitle>
                        <div className="text-sm text-stone-500 mt-1">
                            <span className="text-stone-400">Assunto:</span> {previewSubject}
                        </div>
                    </DialogHeader>
                    <iframe
                        title="email-preview"
                        srcDoc={previewHtml}
                        className="w-full h-[64vh] border-t border-stone-200 bg-white"
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TemplatesAdmin;
